import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { debounce, type DebouncedFunc } from 'lodash';

import { readCurrent } from './readCurrent';

export { type DebouncedFunc };

export function useDebounce<
  T extends (
    ...args: any // oxlint-disable-line typescript/no-explicit-any
  ) => any, // oxlint-disable-line typescript/no-explicit-any
>(callback: T, delay: number): DebouncedFunc<T> {
  const callbackRef = useRef(callback);
  const debouncedRef = useRef<DebouncedFunc<T> | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  });

  useLayoutEffect(() => {
    const debounced = debounce(((...args: Parameters<T>) => callbackRef.current(...args)) as T, delay);
    debouncedRef.current = debounced;
    return () => {
      debounced.cancel();
    };
  }, [delay]);

  const [api] = useState(() => {
    const invoke = (...args: Parameters<T>): ReturnType<T> | undefined => readCurrent(debouncedRef)?.(...args);
    invoke.cancel = () => {
      readCurrent(debouncedRef)?.cancel();
    };
    invoke.flush = () => {
      const debounced = readCurrent(debouncedRef);
      if (debounced === null) {
        throw new Error('useDebounce flush called before the debounced function was installed');
      }
      return debounced.flush();
    };
    const debounced: DebouncedFunc<T> = invoke;
    return debounced;
  });

  return api;
}
